import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderGridCombineOrders } from '../../../store/orders/grid/orders.grid.header.actions';
import { useFedexHalOrderSelectedDisabled } from '../disabled-hooks/use-fedex-hal-order-selected-disabled';
import { useMoreThanOneOrderSelectedDisabled } from '../disabled-hooks/use-more-than-one-order-selected-disabled';
import Global from '../../../store/global-selectors';
import { ComponentStates } from '../../../enums/component-states';
import { getActualSalesOrderIds } from '../../../store/orders/orders.helpers';
import { useCombineSplitOrdersShipmentsFeatureGate } from '../../../feature-gates/use-combine-split-orders-shipments-feature-gate';
import { useContainsDemoOrder } from '../../../demos/hooks/use-contains-demo-order';
import { messages } from './messages';

const getDisabledInfo = ({
  featureGate,
  disabled,
  disabledMessage,
  isCombineShipmentRestricted,
  moreThanOneOrderSelectedDisabled,
  hasDemoOrder,
}) => {
  let isDisabled = false;
  let disabledReasonMessageDescriptor = null;

  if (!featureGate.isFeatureEnabled) {
    isDisabled = true;
    disabledReasonMessageDescriptor = featureGate.disabledMessage;
  } else if (disabled) {
    isDisabled = true;
    disabledReasonMessageDescriptor = disabledMessage;
  } else if (isCombineShipmentRestricted) {
    isDisabled = true;
    disabledReasonMessageDescriptor = messages.shipTogetherRestrictedReason;
  } else if (moreThanOneOrderSelectedDisabled) {
    isDisabled = true;
  } else if (hasDemoOrder) {
    isDisabled = true;
    disabledReasonMessageDescriptor = messages.isDemoDisabledMessage;
  }

  return {
    isDisabled,
    disabledReasonMessageDescriptor,
  };
};

export const useShipTogether = () => {
  const dispatch = useDispatch();
  const { disabled, message } = useFedexHalOrderSelectedDisabled();
  const moreThanOneOrderSelectedDisabled = useMoreThanOneOrderSelectedDisabled();
  const selectedOrdersIds = useSelector(Global.selectSelectedOrdersIds);
  const hasDemoOrder = useContainsDemoOrder(selectedOrdersIds);
  const actualSelectedOrdersIds = useSelector((state) => getActualSalesOrderIds(state, selectedOrdersIds));
  const salesOrders = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByIds(state, {
      salesOrderIds: actualSelectedOrdersIds,
    }),
  );
  const isCombineShipmentRestricted = salesOrders.some(
    ({ restrictions }) => restrictions?.combineShipments?.componentState === ComponentStates.Disabled,
  );

  const featureGate = useCombineSplitOrdersShipmentsFeatureGate();

  const { isDisabled, disabledReasonMessageDescriptor } = getDisabledInfo({
    featureGate,
    disabled,
    disabledMessage: message,
    isCombineShipmentRestricted,
    moreThanOneOrderSelectedDisabled,
    hasDemoOrder,
  });

  const actionHandler = useCallback(() => {
    dispatch(orderGridCombineOrders());
  }, [dispatch]);

  return useMemo(() => {
    return {
      actionHandler,
      nameDescriptor: messages.orderGridOtherActionsCombine,
      isDisabled,
      disabledReason: disabledReasonMessageDescriptor,
    };
  }, [actionHandler, disabledReasonMessageDescriptor, isDisabled]);
};
